import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { searchByUpload, enhanceImage, removeBackground } from '../services/api';

/**
 * ImageUpload Component
 * Handles image upload with drag-and-drop, preview, and search functionality
 */
const ImageUpload = ({ onSearchComplete, onPreviewChange }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [enhancedImage, setEnhancedImage] = useState(null);
  const [backgroundRemovedImage, setBackgroundRemovedImage] = useState(null);
  const [enhancing, setEnhancing] = useState(false);
  const [removingBackground, setRemovingBackground] = useState(false);
  const [showBackgroundRemoved, setShowBackgroundRemoved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const enhancedImageRef = useRef(null);

  // Constants
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

  /**
   * Validate image file
   */
  const validateFile = (file) => {
    if (!file) {
      return 'No file selected';
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Please upload a valid image file (JPEG, PNG, GIF, or WebP)';
    }

    if (file.size > MAX_FILE_SIZE) {
      return `File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`;
    }

    return null;
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = async (file) => {
    setError(null);
    setEnhancedImage(null);
    setBackgroundRemovedImage(null);
    setShowBackgroundRemoved(false);

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSelectedFile(file);

    // Create preview for original image
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  /**
   * Handle file input change
   */
  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Handle drag events
   */
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Convert base64 data URL to Blob
   */
  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  /**
   * Handle image enhancement
   */
  const handleEnhance = async () => {
    if (!selectedFile) {
      setError('Please select an image first');
      return;
    }

    setEnhancing(true);
    setError(null);

    try {
      console.log('Enhancing image...');
      const enhanceResult = await enhanceImage(selectedFile);

      if (enhanceResult.success && enhanceResult.enhancedImage) {
        setEnhancedImage(enhanceResult.enhancedImage);
        console.log('Image enhanced successfully:', enhanceResult.imageInfo);
      }
    } catch (err) {
      console.error('Enhancement failed:', err);
      setError('Failed to enhance image: ' + (err.message || 'Unknown error'));
    } finally {
      setEnhancing(false);
    }
  };

  /**
   * Handle search
   */
  const handleSearch = async () => {
    if (!selectedFile) {
      setError('Please select an image first');
      return;
    }

    setUploading(true);
    setError(null);

    // Determine which image to use and how to send it
    let imageToSend;
    let searchType;

    try {

      if (showBackgroundRemoved && backgroundRemovedImage) {
        // Use background-removed (needs base64 conversion)
        imageToSend = dataURLtoBlob(backgroundRemovedImage);
        searchType = 'backgroundRemoved';
      } else if (enhancedImage) {
        // Use enhanced image
        imageToSend = dataURLtoBlob(enhancedImage);
        searchType = 'enhanced';
      } else {
        // Use original image (send the actual File object to avoid base64 conversion)
        imageToSend = selectedFile;
        searchType = 'original';
      }

      console.log(`Searching with ${searchType} image...`);

      // Pass all image URLs for saving to history
      const imageUrls = {
        originalImageUrl: preview,
        enhancedImageUrl: enhancedImage,
        backgroundRemovedImageUrl: backgroundRemovedImage,
        searchedImageType: searchType  // Track which image was actually searched
      };

      console.log('🔍 ImageUpload sending imageUrls:', {
        hasOriginal: !!preview,
        hasEnhanced: !!enhancedImage,
        hasBackgroundRemoved: !!backgroundRemovedImage,
        searchedImageType: searchType
      });

      const results = await searchByUpload(imageToSend, selectedFile.name, imageUrls);

      console.log('✅ ImageUpload: Search completed successfully', {
        resultsCount: results?.results?.length || 0,
        filename: selectedFile.name,
      });

      // Pass ALL data to parent, including images
      onSearchComplete({
        results: results,
        originalImage: preview,
        enhancedImage: enhancedImage,
        backgroundRemovedImage: backgroundRemovedImage,
        showBackgroundRemoved: showBackgroundRemoved,
        selectedFileName: selectedFile.name,
      });
    } catch (err) {
      console.error('❌ ImageUpload: Search error caught:', {
        message: err.message,
        name: err.name,
        stack: err.stack,
        filename: selectedFile?.name,
        fileSize: imageToSend?.size,
        searchType,
        timestamp: new Date().toISOString(),
      });
      setError(err.message || 'Failed to search. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  /**
   * Handle background removal or toggle
   */
  const handleBackgroundRemoval = async () => {
    // If background removed image already exists, toggle between versions
    if (backgroundRemovedImage) {
      setShowBackgroundRemoved(!showBackgroundRemoved);
      return;
    }

    // Otherwise, remove background from enhanced image
    if (!enhancedImage) {
      setError('Please enhance the image first before removing background');
      return;
    }

    setRemovingBackground(true);
    setError(null);

    try {
      // Convert enhanced image (base64) to Blob for upload
      const enhancedBlob = dataURLtoBlob(enhancedImage);

      console.log('Removing background...');
      const result = await removeBackground(enhancedBlob, selectedFile.name);

      if (result.success && result.image) {
        setBackgroundRemovedImage(result.image);
        setShowBackgroundRemoved(true);
        console.log('Background removed successfully:', result.imageInfo);
      }
    } catch (err) {
      console.error('Background removal failed:', err);
      setError('Failed to remove background: ' + (err.message || 'Unknown error'));
    } finally {
      setRemovingBackground(false);
    }
  };

  /**
   * Handle crop image to viewport
   */
  const handleCrop = () => {
    // Get the currently displayed image
    const imageToCrop = (showBackgroundRemoved && backgroundRemovedImage)
      ? backgroundRemovedImage
      : (enhancedImage || preview);

    if (!imageToCrop) {
      setError('No image available to crop');
      return;
    }

    try {
      // Get viewport dimensions from the container
      const container = enhancedImageRef.current?.parentElement;
      if (!container) {
        setError('Unable to get viewport dimensions');
        return;
      }

      const viewportWidth = container.offsetWidth || 400;
      const viewportHeight = container.offsetHeight || 400;

      // Create an image element to get dimensions
      const img = new Image();
      img.onload = () => {
        // With objectFit: 'cover', the image fills the container
        // Calculate the visible portion based on zoom and pan
        const scale = zoom;

        // The displayed image size (scaled)
        const displayedWidth = img.width * scale;
        const displayedHeight = img.height * scale;

        // Calculate what portion of the original image is visible in the viewport
        // The pan values represent the offset in viewport coordinates
        // We need to convert this to image coordinates

        // Calculate the visible area in original image coordinates
        const visibleWidth = img.width / scale;
        const visibleHeight = img.height / scale;

        // Calculate the center of the visible area in original image coordinates
        // Pan is applied after scaling, so we need to account for that
        const centerX = img.width / 2;
        const centerY = img.height / 2;

        // Convert pan from viewport pixels to original image pixels
        // Pan is in viewport coordinates, but we need it in original image coordinates
        const panXInImage = (pan.x / viewportWidth) * visibleWidth;
        const panYInImage = (pan.y / viewportHeight) * visibleHeight;

        // Calculate source rectangle (what part of the original image to crop)
        const sourceX = Math.max(0, centerX - visibleWidth / 2 - panXInImage);
        const sourceY = Math.max(0, centerY - visibleHeight / 2 - panYInImage);
        const sourceWidth = Math.min(visibleWidth, img.width - sourceX);
        const sourceHeight = Math.min(visibleHeight, img.height - sourceY);

        // Create canvas for cropping
        const canvas = document.createElement('canvas');
        canvas.width = viewportWidth;
        canvas.height = viewportHeight;
        const ctx = canvas.getContext('2d');

        // Draw the cropped portion, scaling to fill the viewport
        ctx.drawImage(
          img,
          sourceX, sourceY, sourceWidth, sourceHeight,
          0, 0, viewportWidth, viewportHeight
        );

        // Convert canvas to base64
        const croppedImage = canvas.toDataURL('image/png');

        // Update the appropriate image with cropped version
        if (enhancedImage) {
          setEnhancedImage(croppedImage);
        } else {
          // If no enhanced image, set it as the enhanced image
          setEnhancedImage(croppedImage);
        }

        // If background removed image exists, crop it with the same parameters
        if (backgroundRemovedImage) {
          const bgImg = new Image();
          bgImg.onload = () => {
            const bgCanvas = document.createElement('canvas');
            bgCanvas.width = viewportWidth;
            bgCanvas.height = viewportHeight;
            const bgCtx = bgCanvas.getContext('2d');

            // Calculate same crop area for background removed image
            const bgVisibleWidth = bgImg.width / scale;
            const bgVisibleHeight = bgImg.height / scale;
            const bgCenterX = bgImg.width / 2;
            const bgCenterY = bgImg.height / 2;
            const bgPanXInImage = (pan.x / viewportWidth) * bgVisibleWidth;
            const bgPanYInImage = (pan.y / viewportHeight) * bgVisibleHeight;
            const bgSourceX = Math.max(0, bgCenterX - bgVisibleWidth / 2 - bgPanXInImage);
            const bgSourceY = Math.max(0, bgCenterY - bgVisibleHeight / 2 - bgPanYInImage);
            const bgSourceWidth = Math.min(bgVisibleWidth, bgImg.width - bgSourceX);
            const bgSourceHeight = Math.min(bgVisibleHeight, bgImg.height - bgSourceY);

            bgCtx.drawImage(
              bgImg,
              bgSourceX, bgSourceY, bgSourceWidth, bgSourceHeight,
              0, 0, viewportWidth, viewportHeight
            );

            const croppedBgImage = bgCanvas.toDataURL('image/png');
            setBackgroundRemovedImage(croppedBgImage);
          };
          bgImg.src = backgroundRemovedImage;
        }

        // Reset zoom and pan after cropping
        setZoom(1);
        setPan({ x: 0, y: 0 });
        setShowBackgroundRemoved(false);

        console.log('Image cropped successfully');
      };
      img.src = imageToCrop;
    } catch (err) {
      console.error('Crop failed:', err);
      setError('Failed to crop image: ' + (err.message || 'Unknown error'));
    }
  };

  /**
   * Handle change image
   */
  const handleChangeImage = () => {
    setSelectedFile(null);
    setPreview(null);
    setEnhancedImage(null);
    setBackgroundRemovedImage(null);
    setShowBackgroundRemoved(false);
    setError(null);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Handle zoom change
   */
  const handleZoomChange = (e) => {
    setZoom(parseFloat(e.target.value));
  };

  /**
   * Reset zoom, pan, and background state
   */
  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setShowBackgroundRemoved(false);
  };

  /**
   * Handle pan start
   */
  const handlePanStart = (e) => {
    const currentImage = enhancedImage || preview;
    if (!currentImage || zoom === 1) return;

    setIsPanning(true);
    setPanStart({
      x: e.clientX - pan.x,
      y: e.clientY - pan.y
    });
  };

  /**
   * Handle pan move
   */
  const handlePanMove = (e) => {
    if (!isPanning) return;

    const newX = e.clientX - panStart.x;
    const newY = e.clientY - panStart.y;

    // Calculate max pan distance based on zoom level
    const maxPan = ((zoom - 1) * 200); // Half of container width

    // Constrain pan within bounds
    const constrainedX = Math.max(-maxPan, Math.min(maxPan, newX));
    const constrainedY = Math.max(-maxPan, Math.min(maxPan, newY));

    setPan({ x: constrainedX, y: constrainedY });
  };

  /**
   * Handle pan end
   */
  const handlePanEnd = () => {
    setIsPanning(false);
  };

  /**
   * Open file dialog
   */
  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  // Get the currently displayed image
  const displayedImage = (showBackgroundRemoved && backgroundRemovedImage)
    ? backgroundRemovedImage
    : (enhancedImage || preview);

  // Styles
  const styles = {
    container: {
      width: '100%',
      ...(preview ? {} : { maxWidth: '600px' }),
      margin: '0 auto',
    },
    dropZone: {
      border: `2px solid ${isDragging ? '#3b82f6' : '#d1d5db'}`,
      borderRadius: '12px',
      padding: '40px 20px',
      textAlign: 'center',
      backgroundColor: isDragging ? '#eff6ff' : '#f9fafb',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    dropZoneActive: {
      borderColor: '#3b82f6',
      backgroundColor: '#eff6ff',
    },
    dropZoneIcon: {
      width: '48px',
      height: '48px',
      marginBottom: '16px',
      display: 'inline-block',
    },
    dropZoneTitle: {
      fontSize: '25px',
      fontWeight: '600',
      color: '#3A3A3A',
      marginBottom: '8px',
    },
    dropZoneText: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '4px',
    },
    hiddenInput: {
      display: 'none',
    },
    previewContainer: {
      marginTop: '24px',
      textAlign: 'center',
    },
    imagesGrid: {
      display: 'flex',
      gap: '100px',
      justifyContent: 'center',
      alignItems: 'flex-start',
      marginBottom: '20px',
    },
    imageColumn: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px',
    },
    imageLabel: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    previewWrapper: {
      position: 'relative',
      flex: '0 0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '400px',
    },
    preview: {
      maxWidth: '100%',
      maxHeight: '100%',
      borderRadius: '12px',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
      display: 'block',
      objectFit: 'contain',
    },
    enhancedPreview: {
      width: '100%',
      height: '100%',
      borderRadius: '12px',
      display: 'block',
      objectFit: 'cover',
      transition: 'transform 0.1s ease-out',
      cursor: 'move',
    },
    zoomControls: {
      marginTop: '12px',
      backgroundColor: '#ffffff',
      padding: '12px 16px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      width: '400px',
    },
    zoomLabel: {
      fontSize: '12px',
      fontWeight: '600',
      color: '#374151',
      minWidth: '35px',
    },
    zoomSlider: {
      flex: '1',
      height: '4px',
      borderRadius: '2px',
      outline: 'none',
      cursor: 'pointer',
    },
    resetButton: {
      padding: '4px 12px',
      fontSize: '12px',
      fontWeight: '600',
      color: '#6b7280',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    resetButtonHover: {
      backgroundColor: '#e5e7eb',
      color: '#374151',
    },
    backgroundButton: {
      padding: '6px',
      fontSize: '12px',
      fontWeight: '600',
      color: '#6b7280',
      backgroundColor: 'transparent',
      border: '2px solid transparent',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '37px',
      minHeight: '37px',
    },
    backgroundButtonDisabled: {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
    backgroundButtonActive: {
      backgroundColor: '#d1fae5',
      borderColor: '#10b981',
    },
    backgroundButtonEnhance: {
      backgroundColor: '#dbeafe',
      borderColor: '#3b82f6',
    },
    smallSpinner: {
      width: '16px',
      height: '16px',
      border: '2px solid #e5e7eb',
      borderTop: '2px solid #3b82f6',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    },
    enhancedImageWrapper: {
      position: 'relative',
      flex: '0 0 auto',
      width: '400px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f9fafb',
      borderRadius: '12px',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
      overflow: 'hidden',
    },
    enhancedImagePlaceholder: {
      color: '#9ca3af',
      fontSize: '16px',
      fontWeight: '500',
    },
    deleteIcon: {
      position: 'absolute',
      top: '12px',
      right: '12px',
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      backgroundColor: 'rgba(239, 68, 68, 0.9)',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px',
      color: '#ffffff',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    },
    deleteIconHover: {
      backgroundColor: 'rgba(220, 38, 38, 1)',
      transform: 'scale(1.1)',
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      marginTop: '80px',
      justifyContent: 'center',
    },
    button: {
      padding: '12px 60px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    primaryButton: {
      backgroundColor: '#B8A88F',
      color: '#ffffff',
    },
    primaryButtonHover: {
      backgroundColor: '#B8A88F',
    },
    primaryButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
    secondaryButton: {
      backgroundColor: '#f3f4f6',
      color: '#374151',
    },
    secondaryButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    error: {
      marginTop: '16px',
      padding: '12px 16px',
      backgroundColor: '#fee2e2',
      border: '1px solid #fecaca',
      borderRadius: '8px',
      color: '#991b1b',
      fontSize: '14px',
      textAlign: 'center',
    },
    spinner: {
      width: '20px',
      height: '20px',
      border: '3px solid #ffffff',
      borderTop: '3px solid transparent',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
  };

  // Add spinner animation
  React.useEffect(() => {
    const styleSheet = document.styleSheets[0];
    const keyframes = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    try {
      styleSheet.insertRule(keyframes, styleSheet.cssRules.length);
    } catch (e) {
      // Rule might already exist
    }
  }, []);

  // Notify parent when preview state changes
  React.useEffect(() => {
    if (onPreviewChange) {
      onPreviewChange(!!preview);
    }
  }, [preview, onPreviewChange]);

  return (
    <div style={styles.container}>
      {!preview ? (
        // Drop zone
        <div
          style={styles.dropZone}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={openFileDialog}
        >
          <img
            src="/assets/icon_export.png"
            alt="Upload"
            style={styles.dropZoneIcon}
          />
          <div style={styles.dropZoneTitle}>
            {isDragging ? 'Drop image here' : 'Upload Jewelry Image'}
          </div>
          <div style={styles.dropZoneText}>
            Drag and drop an image here, or click to select
          </div>
          <div style={styles.dropZoneText}>
            Supports: JPEG, PNG, GIF, WebP (max 10MB)
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleInputChange}
            style={styles.hiddenInput}
          />
        </div>
      ) : (
        // Preview and actions
        <div style={styles.previewContainer}>
          <div style={styles.imagesGrid}>
            {/* Original uploaded image */}
            <div style={styles.imageColumn}>
              <div style={styles.imageLabel}>Original Image</div>
              <div style={styles.previewWrapper}>
                <img src={preview} alt="Preview" style={styles.preview} />
                <button
                  style={styles.deleteIcon}
                  onClick={handleChangeImage}
                  disabled={uploading || enhancing}
                  onMouseEnter={(e) => {
                    if (!uploading && !enhancing) {
                      Object.assign(e.target.style, styles.deleteIconHover);
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploading && !enhancing) {
                      e.target.style.backgroundColor = styles.deleteIcon.backgroundColor;
                      e.target.style.transform = 'scale(1)';
                    }
                  }}
                  title="Remove image"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Enhanced image container */}
            <div style={styles.imageColumn}>
              <div style={styles.imageLabel}>
                {showBackgroundRemoved ? 'Background Removed' : (enhancedImage ? 'Enhanced Image' : 'Original Image')}
              </div>
              <div
                style={styles.enhancedImageWrapper}
                onMouseMove={handlePanMove}
                onMouseUp={handlePanEnd}
                onMouseLeave={handlePanEnd}
              >
                {enhancing ? (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      ...styles.spinner,
                      width: '40px',
                      height: '40px',
                      border: '4px solid #e5e7eb',
                      borderTop: '4px solid #B8A88F',
                      marginBottom: '12px'
                    }} />
                    <div style={{ color: '#6b7280', fontSize: '14px' }}>
                      Enhancing image...
                    </div>
                  </div>
                ) : (showBackgroundRemoved && backgroundRemovedImage) ? (
                  <img
                    ref={enhancedImageRef}
                    src={backgroundRemovedImage}
                    alt="Background Removed"
                    style={{
                      ...styles.enhancedPreview,
                      transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                      cursor: zoom > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default',
                    }}
                    onMouseDown={handlePanStart}
                    draggable={false}
                  />
                ) : enhancedImage ? (
                  <img
                    ref={enhancedImageRef}
                    src={enhancedImage}
                    alt="Enhanced"
                    style={{
                      ...styles.enhancedPreview,
                      transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                      cursor: zoom > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default',
                    }}
                    onMouseDown={handlePanStart}
                    draggable={false}
                  />
                ) : (
                  <img
                    ref={enhancedImageRef}
                    src={preview}
                    alt="Original"
                    style={{
                      ...styles.enhancedPreview,
                      transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                      cursor: zoom > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default',
                    }}
                    onMouseDown={handlePanStart}
                    draggable={false}
                  />
                )}
              </div>

              {/* Zoom Controls - Below Image */}
              {preview && !enhancing && (
                <div style={styles.zoomControls}>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.1"
                    value={zoom}
                    onChange={handleZoomChange}
                    style={styles.zoomSlider}
                  />
                  <button
                    style={{
                      ...styles.backgroundButton,
                      ...(enhancing || !selectedFile ? styles.backgroundButtonDisabled : {}),
                      ...(enhancedImage ? styles.backgroundButtonEnhance : {}),
                    }}
                    onClick={handleEnhance}
                    disabled={enhancing || !selectedFile}
                    onMouseEnter={(e) => {
                      if (!enhancing && selectedFile) {
                        e.target.style.backgroundColor = '#e0e0e0';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!enhancing && selectedFile) {
                        e.target.style.backgroundColor = enhancedImage ? '#dbeafe' : 'transparent';
                      }
                    }}
                    title={enhancedImage ? 'Image enhanced' : 'Enhance image quality'}
                  >
                    {enhancing ? (
                      <div style={styles.smallSpinner} />
                    ) : (
                      <span style={{ fontSize: '20px' }}>✨</span>
                    )}
                  </button>
                  <button
                    style={{
                      ...styles.backgroundButton,
                      ...(removingBackground || !enhancedImage ? styles.backgroundButtonDisabled : {}),
                      ...(showBackgroundRemoved ? styles.backgroundButtonActive : {}),
                    }}
                    onClick={handleBackgroundRemoval}
                    disabled={removingBackground || !enhancedImage}
                    onMouseEnter={(e) => {
                      if (!removingBackground && enhancedImage) {
                        e.target.style.backgroundColor = '#e0e0e0';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!removingBackground && enhancedImage) {
                        e.target.style.backgroundColor = showBackgroundRemoved ? '#d1fae5' : 'transparent';
                      }
                    }}
                    title={backgroundRemovedImage ? (showBackgroundRemoved ? 'Show enhanced' : 'Show no background') : 'Remove background'}
                  >
                    {removingBackground ? (
                      <div style={styles.smallSpinner} />
                    ) : (
                      <img
                        src="/assets/icon_background.png"
                        alt="Background"
                        style={{
                          width: '25px',
                          height: '25px',
                          objectFit: 'contain',
                        }}
                      />
                    )}
                  </button>
                  <button
                    style={{
                      ...styles.backgroundButton,
                      ...(!displayedImage ? styles.backgroundButtonDisabled : {}),
                    }}
                    onClick={handleCrop}
                    disabled={!displayedImage}
                    onMouseEnter={(e) => {
                      if (displayedImage) {
                        e.target.style.backgroundColor = '#e0e0e0';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (displayedImage) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                    title="Crop image to viewport"
                  >
                    <img
                      src="/assets/icons-crop.png"
                      alt="Crop"
                      style={{
                        width: '25px',
                        height: '25px',
                        objectFit: 'contain',
                      }}
                    />
                  </button>
                  <button
                    style={styles.resetButton}
                    onClick={handleResetZoom}
                    onMouseEnter={(e) => {
                      Object.assign(e.target.style, styles.resetButtonHover);
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = styles.resetButton.backgroundColor;
                      e.target.style.color = styles.resetButton.color;
                    }}
                    disabled={zoom === 1 && pan.x === 0 && pan.y === 0 && !showBackgroundRemoved}
                  >
                    Reset
                  </button>
                </div>
              )}
            </div>
          </div>

          <div style={styles.buttonGroup}>
            <button
              style={{
                ...styles.button,
                ...styles.primaryButton,
                ...(uploading || enhancing || removingBackground ? styles.primaryButtonDisabled : {}),
              }}
              onClick={handleSearch}
              disabled={uploading || enhancing || removingBackground}
              onMouseEnter={(e) => {
                if (!uploading && !enhancing && !removingBackground) {
                  e.target.style.backgroundColor = styles.primaryButtonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (!uploading && !enhancing && !removingBackground) {
                  e.target.style.backgroundColor = styles.primaryButton.backgroundColor;
                }
              }}
            >
              {uploading ? (
                <>
                  <div style={styles.spinner} />
                  Searching...
                </>
              ) : (
                <>
                  {zoom > 1 ? 'Crop and Search' : 'Search'}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Error display */}
      {error && <div style={styles.error}>❌ {error}</div>}
    </div>
  );
};

// PropTypes validation
ImageUpload.propTypes = {
  onSearchComplete: PropTypes.func.isRequired,
  onPreviewChange: PropTypes.func,
};

export default ImageUpload;
